import React, { useEffect, useState, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import withRouter from "../Components/Common/withRouter";

// Components
import Header, { BreadcrumbProvider } from "./Header";
import Sidebar from "./Sidebar";
import TeamsSidebar from "./VistaSidebar";
import FullPageLoader from "../Components/Common/FullPageLoader";
import { useGlobalStudy } from "../context/StudyContext";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import type { Dispatch } from "@reduxjs/toolkit";

// Actions
import {
  changeLayout,
  changeSidebarTheme,
  changeLayoutMode,
  changeLayoutWidth,
  changeLayoutPosition,
  changeTopbarTheme,
  changeLeftsidebarSizeType,
  changeLeftsidebarViewType,
  changeSidebarImageType,
  changeSidebarVisibility,
} from "../slices/thunks";
import { SCANNING_PATHS } from "Routes/paths";
import LicenseExpiryAlert from "./LicenseExpiryAlert";

/* ---------- Types ---------- */

interface LayoutState {
  layoutType: string;
  leftSidebarType: string;
  layoutModeType: string;
  layoutWidthType: string;
  layoutPositionType: string;
  topbarThemeType: string;
  leftsidbarSizeType: string;
  leftSidebarViewType: string;
  leftSidebarImageType: string;
  preloader: boolean;
  sidebarVisibilitytype: string;
}

interface RootState {
  Layout: LayoutState;
}

interface LayoutProps {
  children?: ReactNode;
  showHeader?: boolean;
}

/* ---------- Component ---------- */

const Layout: React.FC<LayoutProps> = (props) => {
  const dispatch: Dispatch<any> = useDispatch();
  const location = useLocation();
  const { isStudyLoading } = useGlobalStudy();
  const isSessionPage = location.pathname.includes(SCANNING_PATHS.SESSION);

  const selectLayoutState = (state: RootState) => state.Layout;

  const selectLayoutProperties = createSelector(
    selectLayoutState,
    (layout) => ({
      layoutType: layout.layoutType,
      leftSidebarType: layout.leftSidebarType,
      layoutModeType: layout.layoutModeType,
      layoutWidthType: layout.layoutWidthType,
      layoutPositionType: layout.layoutPositionType,
      topbarThemeType: layout.topbarThemeType,
      leftsidbarSizeType: layout.leftsidbarSizeType,
      leftSidebarViewType: layout.leftSidebarViewType,
      leftSidebarImageType: layout.leftSidebarImageType,
      preloader: layout.preloader,
      sidebarVisibilitytype: layout.sidebarVisibilitytype,
    }),
  );

  const {
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    preloader,
    sidebarVisibilitytype,
  } = useSelector(selectLayoutProperties);

  /* ---------- Apply layout settings ---------- */
  useEffect(() => {
    if (
      layoutType ||
      leftSidebarType ||
      layoutModeType ||
      layoutWidthType ||
      layoutPositionType ||
      topbarThemeType ||
      leftsidbarSizeType ||
      leftSidebarViewType ||
      leftSidebarImageType ||
      sidebarVisibilitytype
    ) {
      window.dispatchEvent(new Event("resize"));

      dispatch(changeLeftsidebarViewType(leftSidebarViewType));
      dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
      dispatch(changeSidebarTheme(leftSidebarType));
      dispatch(changeLayoutMode(layoutModeType));
      dispatch(changeLayoutWidth(layoutWidthType));
      dispatch(changeLayoutPosition(layoutPositionType));
      dispatch(changeTopbarTheme(topbarThemeType));
      dispatch(changeLayout(layoutType));
      dispatch(changeSidebarImageType(leftSidebarImageType));
      dispatch(changeSidebarVisibility(sidebarVisibilitytype));
    }
  }, [
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
    dispatch,
  ]);

  /* ---------- Change layout mode ---------- */
  const onChangeLayoutMode = (value: string): void => {
    dispatch(changeLayoutMode(value));
  };

  /* ---------- Header shadow on scroll ---------- */
  const [headerClass, setHeaderClass] = useState<string>("");

  useEffect(() => {
    const scrollNavigation = (): void => {
      const scrollTop = document.documentElement.scrollTop;
      setHeaderClass(scrollTop > 50 ? "topbar-shadow" : "");
    };

    window.addEventListener("scroll", scrollNavigation, true);
    return () => {
      window.removeEventListener("scroll", scrollNavigation, true);
    };
  }, []);

  return (
    <React.Fragment>
      <BreadcrumbProvider>
        {isStudyLoading && (
          <FullPageLoader message="Loading Study Context..." />
        )}

        <div id="layout-wrapper">

 <LicenseExpiryAlert />
          {props.showHeader && (
            <Header
              headerClass={headerClass}
              layoutModeType={layoutModeType}
              onChangeLayoutMode={onChangeLayoutMode}
            />
          )}

          {!isSessionPage &&
            !location.pathname.includes(SCANNING_PATHS.VIEW) &&
            !location.pathname.includes(SCANNING_PATHS.SESSION) &&
            !location.pathname.includes(SCANNING_PATHS.LOGS) && (
              <TeamsSidebar />
            )}

          <div
            className="main-content"
            style={isSessionPage ? { marginLeft: 0 } : undefined}
          >
            {props.children}
          </div>
        </div>
      </BreadcrumbProvider>
    </React.Fragment>
  );
};

export default withRouter(Layout);
